import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SegmentedTabs } from 'components/ui/data-table';
import { PortalHeading } from './PortalHeading';
import { IS_IO } from 'lib/site';

type SettingsView = 'profile' | 'plan' | 'history' | 'mistakes' | 'referrals' | 'subscription';

const VIEW_PATH: Record<SettingsView, string> = {
  profile: '/portal/settings',
  plan: '/portal/settings/plan',
  history: '/portal/settings/history',
  mistakes: '/portal/settings/mistakes',
  referrals: '/portal/settings/referrals',
  subscription: '/portal/settings/subscription',
};

export default function PortalSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const view: SettingsView = pathname.startsWith(VIEW_PATH.history)
    ? 'history'
    : pathname.startsWith(VIEW_PATH.mistakes)
      ? 'mistakes'
    : pathname.startsWith(VIEW_PATH.subscription)
      ? 'subscription'
    : pathname.startsWith(VIEW_PATH.plan)
      ? 'plan'
      : pathname.startsWith(VIEW_PATH.referrals)
        ? 'referrals'
        : 'profile';

  return (
    <div className="flex flex-col">
      <PortalHeading title={t('settings')} description={t('portal.settings_description')} />
      {/* The views are routes: the segment follows the URL and a pick navigates. Full width on phones. */}
      <SegmentedTabs<SettingsView>
        aria-label={t('settings')}
        className="mt-4 flex h-10 w-full self-start md:mt-5 md:inline-flex md:w-auto [&>button]:flex-1 [&>button]:justify-center md:[&>button]:flex-none"
        options={[
          { value: 'profile' as const, label: t('profile') },
          { value: 'subscription' as const, label: t('subscription.tab') },
          { value: 'plan' as const, label: t('studyPlan.tab') },
          { value: 'history' as const, label: t('portal.results_history') },
          { value: 'mistakes' as const, label: t('mistakes.tab') },
          { value: 'referrals' as const, label: t('referral.tab') },
        ].filter((o) => !IS_IO || (o.value !== 'subscription' && o.value !== 'plan' && o.value !== 'referrals' && o.value !== 'mistakes'))}
        value={view}
        onChange={(next) => {
          if (next !== view) navigate(VIEW_PATH[next]);
        }}
      />
      <div className="mt-6">
        <Outlet />
      </div>
    </div>
  );
}
